import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useToast } from '@/hooks/useToast';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { 
  getUserReferralStats, 
  getUserReferralCode, 
  requestPayout,
  getUserPayoutHistory,
  isReferralSystemEnabled
} from '@/lib/referralSystem';
import { config } from '@/lib/config';
import { db } from '@/lib/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Button 
} from '@/components/ui/button';
import { 
  Badge 
} from '@/components/ui/badge';
import { 
  Input 
} from '@/components/ui/input';
import { 
  Label 
} from '@/components/ui/label';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { 
  Users, 
  TrendingUp, 
  Copy, 
  Share2, 
  Gift, 
  Clock, 
  CheckCircle,
  XCircle,
  AlertCircle,
  PoundSterling,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Image,
  LayoutGrid,
  Percent
} from 'lucide-react';
import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';

const POINTS_PER_REQUEST = 10;

const REQUEST_POINT_REWARDS = [
  {
    points: 100,
    title: 'Claim a free banner',
    description: 'A YSR banner for your gigs and venues.',
    icon: Image,
  },
  {
    points: 150,
    title: 'Claim 5 free table talkers',
    description: 'Table talkers to put in front of the crowd.',
    icon: LayoutGrid,
  },
  {
    points: 250,
    title: '+10% more earnings',
    description: 'A boost on what you take home from requests.',
    icon: Percent,
  },
];

function RewardsComingSoonPanel({ points, mobile }) {
  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div className="space-y-4 select-none">
        <Card className={mobile.isMobile ? ysr2Mobile.glowCard : 'bg-gray-800/50 border-gray-700'}>
          <CardContent className={`${mobile.isMobile ? 'p-4' : 'p-6'} text-center`}>
            <p className="text-sm text-blue-100/70">Your Request Points</p>
            <p className={`${mobile.isMobile ? 'text-4xl' : 'text-5xl'} mt-1 font-black tabular-nums text-white`}>
              {points}
            </p>
            <p className="mt-2 text-xs text-blue-100/55">
              Earn {POINTS_PER_REQUEST} points for every paid song request you receive
            </p>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {REQUEST_POINT_REWARDS.map((reward) => {
            const Icon = reward.icon;
            const progress = Math.min(100, Math.round((points / reward.points) * 100));
            const unlocked = points >= reward.points;
            return (
              <Card
                key={reward.points}
                className={mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800/50 border-gray-700'}
              >
                <CardContent className={`${mobile.isMobile ? 'p-4' : 'p-5'}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-violet-400/30 bg-violet-500/15">
                      <Icon className="h-5 w-5 text-violet-200" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className="font-semibold text-white">{reward.title}</h3>
                        <span className="shrink-0 text-xs font-bold tabular-nums text-emerald-300">
                          {reward.points} pts
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-blue-100/65">{reward.description}</p>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                        <div
                          className={`h-full rounded-full ${unlocked ? 'bg-emerald-400' : 'bg-violet-400/80'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="mt-1.5 text-xs text-blue-100/50">
                        {unlocked
                          ? 'Eligible to claim'
                          : `${Math.max(0, reward.points - points)} points to go`}
                      </p>
                      <Button
                        type="button"
                        disabled
                        className="mt-3 w-full rounded-xl bg-violet-600/40 text-white opacity-70"
                      >
                        {unlocked ? 'Claim' : 'Locked'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div
        className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-slate-950/30 backdrop-blur-[1.5px]"
        aria-hidden
      >
        <span className="px-4 text-center text-3xl font-black uppercase tracking-[0.28em] text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.9)] sm:text-4xl">
          Coming Soon
        </span>
      </div>
    </div>
  );
}

const ReferralDashboard = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const mobile = useMobile();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [email, setEmail] = useState('');
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [expandedReferrals, setExpandedReferrals] = useState(new Set());
  const [referralSystemEnabled, setReferralSystemEnabled] = useState(true);
  const [mainTab, setMainTab] = useState('refer');
  const [requestPoints, setRequestPoints] = useState(0);

  useEffect(() => {
    const hydrateSavedPayoutDetails = async () => {
      if (!user?.uid) return;
      try {
        const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
        const saved = profileDoc.exists() ? profileDoc.data() : {};

        setAccountNumber(saved.account_number || profile?.account_number || '');
        setRoutingNumber(saved.sort_code || profile?.sort_code || '');
        setEmail(saved.paypal_email || profile?.paypal_email || '');
      } catch (err) {
        console.warn('Unable to load saved payout details:', err);
        setAccountNumber(profile?.account_number || '');
        setRoutingNumber(profile?.sort_code || '');
        setEmail(profile?.paypal_email || '');
      }
    };

    hydrateSavedPayoutDetails();
  }, [user?.uid, profile?.account_number, profile?.sort_code, profile?.paypal_email]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const requestsQuery = query(
          collection(db, 'requests'),
          where('artist_id', '==', user.uid)
        );
        const snap = await getDocs(requestsQuery);
        const paidCount = snap.docs.filter((docSnap) => {
          const data = docSnap.data() || {};
          return !!data.payment_intent_id && !data.is_trial;
        }).length;
        if (!cancelled) setRequestPoints(paidCount * POINTS_PER_REQUEST);
      } catch (error) {
        console.warn('Unable to load request points:', error);
        if (!cancelled) setRequestPoints(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (user) {
      loadReferralStats();
      loadPayoutHistory();
      checkReferralSystemEnabled();
    }
  }, [user]);

  // Check referral system enabled status
  const checkReferralSystemEnabled = async () => {
    try {
      const enabled = await isReferralSystemEnabled();
      setReferralSystemEnabled(enabled);
    } catch (error) {
      console.error('Error checking referral system status:', error);
      setReferralSystemEnabled(true); // Default to enabled on error
    }
  };

  // Refresh data every 30 seconds to catch updates
  useEffect(() => {
    if (user) {
      let interval;
      
      const startInterval = () => {
        interval = setInterval(() => {
          // Only refresh if page is visible and user is still available
          if (document.visibilityState === 'visible' && user) {
            loadReferralStats();
            loadPayoutHistory();
          }
        }, 30000); // Refresh every 30 seconds
      };
      
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          // Page became visible, start interval and refresh data
          startInterval();
          loadReferralStats();
          loadPayoutHistory();
        } else {
          // Page became hidden, clear interval to prevent background errors
          if (interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      };
      
      // Start the interval initially
      startInterval();
      
      // Listen for visibility changes
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        if (interval) clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user]);

  const loadReferralStats = async () => {
    try {
      // Check if user is still available before proceeding
      if (!user || !user.uid) {
        console.log('🔍 ReferralDashboard: User not available, skipping stats load');
        return;
      }
      
      setLoading(true);
      console.log('🔍 ReferralDashboard: Loading stats for user:', user.uid);
      const referralStats = await getUserReferralStats(user.uid);
      console.log('🔍 ReferralDashboard: Received stats:', referralStats);
      setStats(referralStats);
    } catch (error) {
      console.error('Error loading referral stats:', error);
      // Only show toast if it's not an auth context error
      if (!error.message?.includes('useAuth must be used within an AuthProvider')) {
        toast({
          title: "Error",
          description: "Failed to load referral statistics",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPayoutHistory = async () => {
    try {
      // Check if user is still available before proceeding
      if (!user || !user.uid) {
        console.log('🔍 ReferralDashboard: User not available, skipping payout history load');
        return;
      }
      
      const history = await getUserPayoutHistory(user.uid);
      setPayoutHistory(history);
    } catch (error) {
      console.error('Error loading payout history:', error);
      // Don't show error toast for auth context errors
      if (!error.message?.includes('useAuth must be used within an AuthProvider')) {
        console.log('🔍 ReferralDashboard: Non-auth error in payout history:', error.message);
      }
    }
  };

  const toggleReferralExpansion = (referralId) => {
    const newExpanded = new Set(expandedReferrals);
    if (newExpanded.has(referralId)) {
      newExpanded.delete(referralId);
    } else {
      newExpanded.add(referralId);
    }
    setExpandedReferrals(newExpanded);
  };

  const copyReferralUrl = async () => {
    try {
      const referralUrl = `${config.getBaseUrl()}/signup?ref=${stats.referralCode}`;
      await navigator.clipboard.writeText(referralUrl);
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard"
      });
    } catch (error) {
      console.error('Error copying referral URL:', error);
    }
  };

  const shareReferralLink = async () => {
    const referralLink = `${config.getBaseUrl()}/signup?ref=${stats.referralCode}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Join Your Song Request',
          text: 'Join me on Your Song Request and start earning!',
          url: referralLink
        });
      } else {
        await navigator.clipboard.writeText(referralLink);
        toast({
          title: "Link Copied!",
          description: "Referral link copied to clipboard"
        });
      }
    } catch (error) {
      console.error('Error sharing referral link:', error);
    }
  };

  const handlePayoutRequest = async () => {
    try {
      if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
        toast({
          title: "Invalid Amount",
          description: "Please enter a valid payout amount",
          variant: "destructive"
        });
        return;
      }

      if (parseFloat(payoutAmount) > stats.pendingEarnings) {
        toast({
          title: "Insufficient Funds",
          description: "Payout amount exceeds pending earnings",
          variant: "destructive"
        });
        return;
      }

      if (paymentMethod === 'bank_transfer' && (!accountNumber.trim() || !routingNumber.trim())) {
        toast({
          title: "Missing bank details",
          description: "Please enter account number and sort code.",
          variant: "destructive"
        });
        return;
      }

      if (paymentMethod === 'paypal' && !email.trim()) {
        toast({
          title: "Missing PayPal email",
          description: "Please enter your PayPal email.",
          variant: "destructive"
        });
        return;
      }

      const paymentDetails = {
        method: paymentMethod,
        accountNumber,
        sortCode: routingNumber,
        routingNumber,
        email
      };

      await requestPayout(user.uid, parseFloat(payoutAmount), paymentDetails);
      
      toast({
        title: "Payout Requested",
        description: "Your payout request has been submitted for review"
      });

      setPayoutDialogOpen(false);
      setPayoutAmount('');
      loadReferralStats();
      loadPayoutHistory();
      
    } catch (error) {
      console.error('Error requesting payout:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to request payout",
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className={`${mobile.isMobile ? ysr2Mobile.shell : ''} flex items-center justify-center p-8 min-h-[50vh]`}>
        {mobile.isMobile && <div className={ysr2Mobile.orbBlue} />}
        {mobile.isMobile && <div className={ysr2Mobile.orbPurple} />}
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-300">Loading referral dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className={`${mobile.isMobile ? `${ysr2Mobile.shell} min-h-[100dvh] px-4 pt-4 pb-28` : ''} space-y-6`}>
      {mobile.isMobile && <div className={ysr2Mobile.orbBlue} />}
      {mobile.isMobile && <div className={ysr2Mobile.orbPurple} />}
      {/* Header + Refer / Rewards tabs */}
      <div className={`${mobile.isMobile ? `${ysr2Mobile.surfaceStrong} relative z-10 p-4` : ''} space-y-4`}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-white">Refer & Reward</h2>
            <div className="mt-1 flex items-center gap-2">
              <Gift className="h-5 w-5 text-blue-200" />
              <p className="text-sm text-blue-100/70">
                {mainTab === 'rewards'
                  ? 'Earn Request Points every time a fan pays for a song'
                  : referralSystemEnabled
                    ? 'Share YSR with artists. Earn rewards from our cut.'
                    : 'Refer other artists to grow the platform'}
              </p>
            </div>
          </div>
          {mainTab === 'refer' && (
            <Button
              onClick={() => {
                loadReferralStats();
                loadPayoutHistory();
              }}
              variant="outline"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>
        <style>{`
          @keyframes ysrRewardsTabGlow {
            0%, 100% {
              box-shadow: 0 0 10px rgba(167, 139, 250, 0.4), 0 0 22px rgba(139, 92, 246, 0.28);
              background-color: rgba(139, 92, 246, 0.2);
            }
            50% {
              box-shadow: 0 0 18px rgba(221, 214, 254, 0.95), 0 0 36px rgba(139, 92, 246, 0.75);
              background-color: rgba(139, 92, 246, 0.4);
            }
          }
        `}</style>
        <div
          className="flex items-center gap-1 rounded-2xl border border-white/10 bg-black/25 p-1 shadow-inner shadow-black/20"
          role="tablist"
          aria-label="Refer and Rewards"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'refer'}
            onClick={() => setMainTab('refer')}
            className={`min-h-[40px] flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
              mainTab === 'refer'
                ? 'bg-blue-500/35 text-white shadow-md'
                : 'text-blue-100/70 hover:bg-white/5'
            }`}
          >
            Refer
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'rewards'}
            onClick={() => setMainTab('rewards')}
            className={`min-h-[40px] flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
              mainTab === 'rewards'
                ? 'bg-blue-500/35 text-white shadow-md'
                : 'text-white ring-1 ring-violet-300/70'
            }`}
            style={
              mainTab === 'rewards'
                ? undefined
                : { animation: 'ysrRewardsTabGlow 1.8s ease-in-out infinite' }
            }
          >
            Rewards
          </button>
        </div>
      </div>

      {mainTab === 'rewards' && (
        <RewardsComingSoonPanel points={requestPoints} mobile={mobile} />
      )}

      {mainTab === 'refer' && (
      <>
      {/* Stats Cards */}
      <div className={`grid ${mobile.isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
        <Card className={mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800/50 border-gray-700'}>
          <CardContent className={`${mobile.isMobile ? 'p-3' : 'p-4'}`}>
            <div className={`flex items-center ${mobile.isMobile ? 'flex-col text-center' : 'justify-between'}`}>
              <div>
                <p className={`${mobile.isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>Total Referrals</p>
                <p className={`${mobile.isMobile ? 'text-xl' : 'text-2xl'} font-bold text-white`}>{stats?.totalReferrals || 0}</p>
              </div>
              <Users className={`${mobile.isMobile ? 'h-6 w-6 mt-1' : 'h-8 w-8'} text-blue-400`} />
            </div>
          </CardContent>
        </Card>

        {referralSystemEnabled && (
          <Card className={mobile.isMobile ? ysr2Mobile.glowCard : 'bg-gray-800/50 border-gray-700'}>
            <CardContent className={`${mobile.isMobile ? 'p-3' : 'p-4'}`}>
              <div className={`flex items-center ${mobile.isMobile ? 'flex-col text-center' : 'justify-between'}`}>
                <div>
                  <p className={`${mobile.isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>Total Earnings</p>
                  <p className={`${mobile.isMobile ? 'text-xl' : 'text-2xl'} font-bold text-white`}>£{stats?.totalEarnings?.toFixed(2) || '0.00'}</p>
                </div>
                <PoundSterling className={`${mobile.isMobile ? 'h-6 w-6 mt-1' : 'h-8 w-8'} text-green-400`} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Referral Code Section */}
      <Card className={mobile.isMobile ? ysr2Mobile.surfaceStrong : 'bg-gray-800/50 border-gray-700'}>
        <CardHeader className={`${mobile.isMobile ? 'pb-3' : ''}`}>
          <CardTitle className={`${mobile.isMobile ? 'text-lg' : 'text-xl'} text-white`}>Your Rewards Link</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {referralSystemEnabled && (
              <div className="rounded-2xl border border-blue-300/20 bg-blue-500/10 p-4">
                <h3 className="text-white font-semibold mb-2">How it works</h3>
                <div className="space-y-2 text-sm text-blue-50/85">
                  <p>Share your link with another artist.</p>
                  <p>When they earn from song requests, you get a 10% reward.</p>
                  <p>Their earnings do not change. Your reward comes from the YSR cut.</p>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="referralUrl" className="text-gray-400">Rewards Link</Label>
              <div className="flex gap-2">
                <Input
                  id="referralUrl"
                  value={stats?.referralCode ? `${config.getBaseUrl()}/signup?ref=${stats.referralCode}` : ''}
                  readOnly
                  className={`${mobile.isMobile ? ysr2Mobile.input : 'bg-gray-700 border-gray-600'} text-white text-sm flex-1`}
                />
                <Button onClick={copyReferralUrl} variant="outline" size="sm">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={shareReferralLink} variant="outline" size="sm" className="flex-1">
                <Share2 className="h-4 w-4 mr-2" />
                Share Your Link
              </Button>
            </div>
          </div>
          <p className={`${mobile.isMobile ? 'text-xs' : 'text-sm'} text-gray-400 mt-2`}>
            {referralSystemEnabled 
              ? "A simple way to help YSR grow while supporting other artists."
              : "Share this link with other artists to help grow the platform"}
          </p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="referrals" className="w-full">
        <TabsList className={`grid w-full ${referralSystemEnabled ? 'grid-cols-2' : 'grid-cols-1'} ${mobile.isMobile ? 'h-10' : ''}`}>
          <TabsTrigger value="referrals" className={`${mobile.isMobile ? 'text-xs px-2' : ''}`}>Referrals</TabsTrigger>
          {referralSystemEnabled && (
            <TabsTrigger value="payouts" className={`${mobile.isMobile ? 'text-xs px-2' : ''}`}>Payouts</TabsTrigger>
          )}
        </TabsList>

        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <Card className={mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800/50 border-gray-700'}>
            <CardHeader>
              <CardTitle className="text-white">Your Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Stage Legend */}
              <div className="mb-6 p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <h4 className="text-white font-semibold mb-3">Referral Progress Stages</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-yellow-400 border-yellow-400">Signed Up</Badge>
                    <span className="text-gray-400">Account created</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-blue-400 border-blue-400">Building Catalog</Badge>
                    <span className="text-gray-400">Added songs</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-purple-400 border-purple-400">Ready to Earn</Badge>
                    <span className="text-gray-400">Gone live</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-green-400 border-green-400">Fully Active</Badge>
                    <span className="text-gray-400">Earning! 🎉</span>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  💡 Hover over any stage badge to see what needs to happen next
                </p>
              </div>
              
              {stats?.referrals?.length > 0 ? (
                <div className="space-y-3">
                  {stats.referrals.map((referral) => {
                    const isExpanded = expandedReferrals.has(referral.id);
                    const artistEarnings = stats.earnings?.filter(earning => earning.referredId === referral.referredId) || [];
                    
                    return (
                      <div key={referral.id} className="border border-gray-700 rounded-lg overflow-hidden">
                        {/* Main Referral Row */}
                        <div 
                          className="flex items-center justify-between p-4 bg-gray-800/30 hover:bg-gray-800/50 cursor-pointer transition-colors"
                          onClick={() => toggleReferralExpansion(referral.id)}
                        >
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="flex items-center space-x-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                              )}
                              <div>
                                <h3 className="text-white font-semibold">
                                  {referral.referredStageName || referral.referredUserName || referral.referredId.substring(0, 8) + '...'}
                                </h3>
                                <p className="text-gray-400 text-sm">
                                  Joined: {referral.createdAt?.toDate?.()?.toLocaleDateString('en-GB') || 'Unknown'}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge 
                                  variant="outline" 
                                  className={`cursor-help ${
                                    referral.signupStageColor === 'green' 
                                      ? 'text-green-400 border-green-400' 
                                      : referral.signupStageColor === 'blue'
                                      ? 'text-blue-400 border-blue-400'
                                      : referral.signupStageColor === 'purple'
                                      ? 'text-purple-400 border-purple-400'
                                      : 'text-yellow-400 border-yellow-400'
                                  }`}
                                >
                                  {referral.signupStageDisplay || 'Signed Up'}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-sm">
                                  {referral.signupStageTooltip || 'They\'ve created an account. Next: Add songs to their catalog'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                            
                            {referralSystemEnabled && (
                              <div className="text-right">
                                <div className="text-green-400 font-semibold">
                                  £{referral.totalEarnings?.toFixed(2) || '0.00'}
                                </div>
                                <div className="text-gray-400 text-xs">
                                  {artistEarnings.length} request{artistEarnings.length !== 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Earnings Details */}
                        {isExpanded && artistEarnings.length > 0 && referralSystemEnabled && (
                          <div className="border-t border-gray-700 bg-gray-900/30">
                            <div className="p-4">
                              <h4 className="text-white font-medium mb-3">Song Request Earnings</h4>
                              <div className="space-y-2">
                                {artistEarnings.map((earning, index) => (
                                  <div key={earning.id || index} className="flex items-center justify-between py-2 px-3 bg-gray-800/30 rounded">
                                    <div>
                                      <div className="text-white text-sm">
                                        {earning.createdAt?.toDate?.()?.toLocaleDateString('en-GB') || 'Unknown Date'}
                                      </div>
                                      <div className="text-gray-400 text-xs">
                                        Song Request #{earning.transactionId?.substring(0, 8) || 'Unknown'}
                                      </div>
                                    </div>
                                    <div className="text-green-400 font-medium">
                                      £{earning.amount?.toFixed(2) || '0.00'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* No earnings message */}
                        {isExpanded && artistEarnings.length === 0 && (
                          <div className="border-t border-gray-700 bg-gray-900/30 p-4">
                            <p className="text-gray-400 text-sm text-center">
                              No song request earnings yet
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">No referrals yet</p>
                  <p className="text-sm text-gray-500">Share your referral code to start earning!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Payouts Tab */}
        <TabsContent value="payouts">
          <Card className={mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800/50 border-gray-700'}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Payouts</CardTitle>
                <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      disabled={!stats?.pendingEarnings || stats.pendingEarnings <= 0}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Request Payout
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`bg-gray-800 border-gray-700 ${mobile.isMobile ? 'max-w-[95vw] mx-4' : ''}`}>
                    <DialogHeader>
                      <DialogTitle className="text-white">Request Payout</DialogTitle>
                    </DialogHeader>
                    <div className={`space-y-4 ${mobile.isMobile ? 'max-h-[80vh] overflow-y-auto' : ''}`}>
                      <div>
                        <Label htmlFor="payoutAmount" className="text-gray-400">Amount</Label>
                        <Input
                          id="payoutAmount"
                          type="number"
                          step="0.01"
                          min="0"
                          max={stats?.pendingEarnings || 0}
                          value={payoutAmount}
                          onChange={(e) => setPayoutAmount(e.target.value)}
                          placeholder="0.00"
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                        <p className="text-sm text-gray-400 mt-1">
                          Available: £{stats?.pendingEarnings?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="paymentMethod" className="text-gray-400">Payment Method</Label>
                        <select
                          id="paymentMethod"
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                        >
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="paypal">PayPal</option>
                          <option value="stripe">Stripe</option>
                        </select>
                      </div>

                      {paymentMethod === 'bank_transfer' && (
                        <>
                          <div>
                            <Label htmlFor="accountNumber" className="text-gray-400">Account Number</Label>
                            <Input
                              id="accountNumber"
                              value={accountNumber}
                              onChange={(e) => setAccountNumber(e.target.value)}
                              placeholder="1234567890"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          <div>
                            <Label htmlFor="routingNumber" className="text-gray-400">Sort Code</Label>
                            <Input
                              id="routingNumber"
                              value={routingNumber}
                              onChange={(e) => setRoutingNumber(e.target.value)}
                              placeholder="12-34-56"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </>
                      )}

                      {paymentMethod === 'paypal' && (
                        <div>
                          <Label htmlFor="email" className="text-gray-400">PayPal Email</Label>
                          <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                        </div>
                      )}

                      <div className="flex gap-2 pt-4">
                        <Button 
                          onClick={handlePayoutRequest}
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                        >
                          Request Payout
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => setPayoutDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {payoutHistory?.length > 0 ? (
                <div className={`${mobile.isMobile ? 'overflow-x-auto' : ''}`}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className={`text-gray-400 ${mobile.isMobile ? 'min-w-[80px]' : ''}`}>Date</TableHead>
                        <TableHead className={`text-gray-400 ${mobile.isMobile ? 'min-w-[80px]' : ''}`}>Amount</TableHead>
                        <TableHead className={`text-gray-400 ${mobile.isMobile ? 'min-w-[80px]' : ''}`}>Method</TableHead>
                        <TableHead className={`text-gray-400 ${mobile.isMobile ? 'min-w-[100px]' : ''}`}>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payoutHistory.map((payout) => (
                        <TableRow key={payout.id}>
                          <TableCell className="text-white">
                            {payout.requestedAt?.toDate?.()?.toLocaleDateString('en-GB') || 'Unknown'}
                          </TableCell>
                          <TableCell className="text-green-400">
                            £{payout.amount?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-gray-400 capitalize">
                            {payout.paymentMethod?.replace('_', ' ')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(payout.status)}
                              <Badge className={getStatusColor(payout.status)}>
                                {payout.status === 'paid' ? 'completed' : payout.status}
                              </Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">No payout requests yet</p>
                  <p className="text-sm text-gray-500">Request a payout when you have pending earnings</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </>
      )}
      </div>
    </TooltipProvider>
  );
};

export default ReferralDashboard;
